// **The composition root's pvp Beekeeper wiring** (`docs/dev/pvp-prototype.md`).
//
// Deliberately its own module rather than another branch inside `SessionController`: the whole
// prototype is one hook, and a hook that answers `undefined` for every non-pvp deployment is a
// smaller thing to delete later than a set of edits scattered through the root.
//
// It **probes nothing**. The discovery flag is resolved once at boot, on the mode probe the client
// already makes (`app/rounds/game-mode.ts`'s `resolveBootSurface`), and arrives here as a plain
// boolean — so no request is added to the live session path, which must stay exactly as it shipped.
//
// Three things must all be true before a seam exists at all: the server advertises pvp, a live
// session token is held, and the store knows which colony is ours. Until then the hook answers
// `undefined`, which `Colony` reads as "render live mode exactly as it always has".

import { useMemo } from 'react';

import { createChatChannel } from './rounds/chat-channel';
import {
  createChatUncertaintyMarkerStore,
  windowSessionStorage,
} from './rounds/chat-uncertainty-marker';
import {
  createLiveChatTransport,
  createPresentationPreviewChatTransport,
} from '../net/live-chat';
import { useClientStore } from '../state/store';
import type { ChatSeam } from '../ui/colony/chat-seam';

export interface PvpChatDeps {
  /** The boot-resolved discovery flag. Absent/false is every shipped deployment. */
  enabled?: boolean;
  /** Same-origin by default, matching the lobby client's own base. */
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  preview?: { presenterToken: string; team: 'red' | 'blue' };
}

/**
 * The pvp chat seam for this session, or `undefined`.
 *
 * The channel is rebuilt only when the credential or the colony changes, so a reconnect on the
 * same session keeps the transcript it already holds.
 */
export function usePvpChatSeam(deps: PvpChatDeps = {}): ChatSeam | undefined {
  const sessionToken = useClientStore((s) => s.sessionToken);
  const colonyId = useClientStore((s) => s.colonyId);
  const { enabled = false, baseUrl = '', fetchImpl } = deps;
  const previewToken = deps.preview?.presenterToken;
  const previewTeam = deps.preview?.team;

  return useMemo(() => {
    if (!enabled || sessionToken === null || colonyId === null) {
      return undefined;
    }
    const transport = previewToken === undefined || previewTeam === undefined
      ? createLiveChatTransport({
          baseUrl,
          colonyId,
          token: sessionToken,
          ...(fetchImpl === undefined ? {} : { fetchImpl }),
        })
      : createPresentationPreviewChatTransport({
          baseUrl,
          team: previewTeam,
          presenterToken: previewToken,
          ...(fetchImpl === undefined ? {} : { fetchImpl }),
        });
    return createChatChannel({
      transport,
      // The pvp channel gets the same uncertainty marker the rounds channels do
      // (`beekeeper-progress-indicator`). Without it a reload after an ambiguous send boots this
      // screen as `idle` — composer live, mutation gate open — for as long as the async rehydrate
      // takes, which is the duplicate-send window the marker exists to close. There is nothing
      // pvp-specific about the exposure: same channel, same transport, same failure.
      //
      // Scoped on `colonyId`, which is this screen's equivalent of the rounds sandbox id: it is
      // what the transport already addresses its requests with, so one tab's colonies can never
      // read each other's marker.
      uncertaintyMarker: createChatUncertaintyMarkerStore(windowSessionStorage(), `pvp:${colonyId}`),
    });
  }, [enabled, sessionToken, colonyId, baseUrl, fetchImpl, previewTeam, previewToken]);
}
